// src/utils/devToggles.js
//
// Per-browser developer toggles surfaced on the Developer Info settings page.
// Reads use `isMockAuthenticatorEnabled`; writes go through
// `setMockAuthenticatorEnabled`, which flushes fhirService's HBA + FHIR token
// caches on transition to prevent the previous mode's cached token from being
// silently reused after a flag flip.

import fhirService from '../services/fhirService';

// snyk:ignore:Hardcoded Non-Cryptographic Secret -- this is a localStorage key name, not a credential
const MOCK_AUTHENTICATOR_KEY = 'useMockAuthenticator';

export function isMockAuthenticatorEnabled() {
  try {
    return typeof window !== 'undefined'
      && window.localStorage?.getItem(MOCK_AUTHENTICATOR_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Set the mock-authenticator flag. Whenever the value transitions, flush
 * fhirService's HBA + FHIR token caches — otherwise the previous mode's
 * cached token would be reused, silently downgrading or upgrading the
 * credential strength of subsequent owner-API calls.
 */
export function setMockAuthenticatorEnabled(enabled) {
  const prev = isMockAuthenticatorEnabled();
  const next = !!enabled;
  try {
    if (next) window.localStorage.setItem(MOCK_AUTHENTICATOR_KEY, 'true');
    else window.localStorage.removeItem(MOCK_AUTHENTICATOR_KEY);
  } catch {
    /* localStorage may be unavailable (private mode) — silently ignore */
  }
  if (prev !== next) {
    try { fhirService.clearTokens(); } catch { /* non-fatal */ }
  }
}

// Hostname allowlist for honoring the mock-authenticator flag.
// Production homeservers MUST NOT honor the flag, even if the localStorage
// key is set — this defends against a curious user permanently breaking
// their healthcare flow on PU/RU and against any hypothetical future
// misconfigured /mock-authenticator endpoint reachable in production.
// Production deployments use *.netsfere.com, which is intentionally NOT
// covered here. The .netsferedev.com pattern subsumes all dev/TU/RU
// subdomains; do not re-add narrower patterns without removing the broad one.
const MOCK_HOST_ALLOWLIST = [
  /^localhost$/i,
  /^127\.0\.0\.1$/,
  /\.netsferedev\.com$/i,
];

export function isMockAuthenticatorHostAllowed(baseUrl) {
  try {
    const host = new URL(baseUrl).hostname;
    return MOCK_HOST_ALLOWLIST.some(rx => rx.test(host));
  } catch {
    return false;
  }
}

export const DEV_TOGGLE_KEYS = { MOCK_AUTHENTICATOR: MOCK_AUTHENTICATOR_KEY };
